import { Component } from '@theme/component';

/**
 * Scentreat "Shop by purpose" tabs.
 * - One tab per collection; panels are server-rendered, JS only toggles visibility.
 * - The tab bar scrolls horizontally; prev/next buttons appear only when it overflows.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLButtonElement} [tabPrev]
 * @property {HTMLButtonElement} [tabNext]
 */

/** @extends {Component<Refs>} */
class StPurposeTabsComponent extends Component {
  requiredRefs = ['tablist', 'tabs', 'panels'];

  #resizeObserver = new ResizeObserver(() => this.#updateTabArrows());

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this.refs.tablist);
    this.refs.tablist.addEventListener('scroll', this.#updateTabArrows, { passive: true });
    this.#updateTabArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.refs.tablist.removeEventListener('scroll', this.#updateTabArrows);
  }

  /**
   * @param {{ index: number }} data
   */
  select(data) {
    const index = Number(data.index);
    this.refs.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active) tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    });
    this.refs.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      if (i === index && panel.scrollLeft) panel.scrollTo({ left: 0 });
    });
  }

  /**
   * Scroll the visible panel by one card (phone card slider, e.g. review cards).
   * @param {{ direction: 'previous' | 'next' }} data
   */
  scrollPanel(data) {
    const panel = this.refs.panels.find((p) => !p.hidden);
    if (!panel) return;
    const card = panel.firstElementChild;
    const gap = parseFloat(getComputedStyle(panel).columnGap) || 12;
    const step = card ? card.getBoundingClientRect().width + gap : panel.clientWidth * 0.86;
    panel.scrollBy({ left: step * (data.direction === 'next' ? 1 : -1), behavior: 'smooth' });
  }

  /**
   * Scroll the tab bar by one viewport width.
   * @param {{ direction: 'previous' | 'next' }} data
   */
  scrollTabs(data) {
    const { tablist } = this.refs;
    const delta = tablist.clientWidth * 0.8 * (data.direction === 'next' ? 1 : -1);
    tablist.scrollBy({ left: delta, behavior: 'smooth' });
  }

  /**
   * Arrow-key navigation between tabs (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  keydown(event) {
    const { tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    this.select({ index: next });
    tabs[next]?.focus();
  }

  #updateTabArrows = () => {
    const { tablist, tabPrev, tabNext } = this.refs;
    if (!tablist) return;
    const overflow = tablist.scrollWidth - tablist.clientWidth;
    const hasOverflow = overflow > 4;
    this.toggleAttribute('data-tabs-overflow', hasOverflow);
    if (tabPrev) tabPrev.disabled = !hasOverflow || tablist.scrollLeft <= 2;
    if (tabNext) tabNext.disabled = !hasOverflow || tablist.scrollLeft >= overflow - 2;
  };
}

if (!customElements.get('st-purpose-tabs-component')) {
  customElements.define('st-purpose-tabs-component', StPurposeTabsComponent);
}
